import {put, call, all} from 'redux-saga/effects';
import ActivityType from "../../entities/activityType";
import * as Actions from "../actions/activityTypeActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ActivityTypeAction = {
    activityTypeId: string;
    activityType: ActivityType;
    activityTypes: ActivityType[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneActivityType(action: ActivityTypeAction) {
	const session = tabSession.captureGeneration();
	try {
		const activityType: ActivityType = yield call(api.findOne, 'activityType', action.activityTypeId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYTYPE_FETCH_ONE_SUCCESS, activityTypeId: action.activityTypeId, activityType, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACTIVITYTYPE_FETCH_ONE_FAILURE, activityTypeId: action.activityTypeId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ActivityTypeAction} action
 */
export function* saveActivityType(action: ActivityTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ACTIVITYTYPE_SAVE saga', action.activityType?.id);

		if (action.activityType === undefined || action.activityType === null) {
			console.error('saveActivityType received a null');
			throw new Error('saveActivityType received a null');
		}

		if (!(action.activityType instanceof ActivityType)) {
			action.activityType = new ActivityType(action.activityType);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.activityType.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.activityType);
			const addResult = yield call(idb.add, 'activityType', action.activityType);
			if (addResult.isErr()) {
				console.error(`⮂ saveActivityType: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertActivityTypeSave(action.activityType, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.ACTIVITYTYPE_SAVE_FAILURE, activityType: action.activityType, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.ACTIVITYTYPE_SAVE, activityType: action.activityType, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertActivityTypeSave(action.activityType, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.ACTIVITYTYPE_SAVE_FAILURE, activityType: action.activityType, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ActivityTypeAction} action
 * @param {ActivityType[]} action.activityTypes
 */
export function* saveManyActivityTypes(action: ActivityTypeAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ ACTIVITYTYPE_SAVE_MANY saga', action.activityTypes.map(activityType => activityType.id).join(','));

        if (!Array.isArray(action.activityTypes)) {
            console.error('saveManyActivityTypes expected an array, received', action.activityTypes);
            throw new Error('saveManyActivityTypes expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveActivityType), under these queue keys
		const dispatchedAddedOns: number[] = action.activityTypes.map(activityType => activityType?.addedOn);
		const revertAll = () => action.activityTypes.map((activityType, i) =>
			put(Actions.revertActivityTypeSave(activityType, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: ActivityType[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.activityTypes.length; i++) {
				const activityType = action.activityTypes[i];
				activityType.persisted = false;
				ensureOperationId(activityType);
				toSave.push(activityType);
			}

			const addResult = yield call(idb.add, 'activityType', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyActivityTypes: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.ACTIVITYTYPE_SAVE_MANY_FAILURE, activityTypes: action.activityTypes, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.ACTIVITYTYPE_SAVE_MANY, activityTypes: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.ACTIVITYTYPE_SAVE_MANY_FAILURE, activityTypes: action.activityTypes, status: 500, err});

			console.error(`Error saving ${action.activityTypes.length} activityTypes to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ActivityTypeAction} action
 */
export function* removeActivityType(action: ActivityTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.activityType === undefined || action.activityType === null || !(action.activityType instanceof ActivityType)) {
			console.error('removeActivityType expected type ActivityType, received', action.activityType);
			reportQueued(false);
			throw new Error('removeActivityType expected type ActivityType');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.activityType.status = 'D';
		action.activityType.addedOn = newHRTimestamp();
		action.activityType.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.activityType);
		const addResult = yield call(idb.add, 'activityType', action.activityType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeActivityType: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.ACTIVITYTYPE_REMOVE_FAILURE, activityType: action.activityType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYTYPE_REMOVE, activityType: action.activityType});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACTIVITYTYPE_REMOVE_FAILURE, activityType: action.activityType, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this ActivityType.
	*
	* @param {ActivityTypeAction} action
	*/
export function* restoreActivityType(action: ActivityTypeAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreActivityType expected getCurrentUserId() to return a value');
        return;
    }

	if (action.activityType === undefined || action.activityType === null) {
        console.error('restoreActivityType expected a ActivityType, received null');
        throw new Error('restoreActivityType expected a ActivityType, received null');
    }
    else if (!(action.activityType instanceof ActivityType)) action.activityType = new ActivityType(action.activityType); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.activityType.status = 'U';
		action.activityType.addedOn = newHRTimestamp();
		action.activityType.addedBy = userId;
		action.activityType.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.activityType);
		const addResult = yield call(idb.add, 'activityType', action.activityType);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreActivityType: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.ACTIVITYTYPE_RESTORE_FAILURE, activityType: action.activityType, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.ACTIVITYTYPE_RESTORE, activityType: action.activityType});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.ACTIVITYTYPE_RESTORE_FAILURE, activityType: action.activityType, status: 500, err});

		console.error('Error restoring from database', err);
	}
}